import React from 'react';
import {AlertTriangleIcon, CheckCheckIcon, CloudIcon, PlusIcon} from '../icons';
import {
    AppliedConstraints, Readiness, isReady, missingLabel, readinessLabel,
} from '../../support/outfitReadiness';

interface Props {
    readiness: Readiness | null | undefined;
    constraints?: AppliedConstraints | null;
    /** Действие «Добавить»: ведёт в конструктор с текущим составом. */
    onComplete?: () => void;
    /** Идёт пересчёт после правки состава — статус временно неактуален. */
    updating?: boolean;
}

/**
 * Первый блок результата подбора: полнота комплекта (ретест 17.09.2026, R01/R11).
 *
 * Порядок подачи важнее оформления: сначала «Комплект полный / Нужно дополнить», затем
 * принятые ограничения и то, что проверить не удалось, и только потом — балл сочетаемости.
 * Раньше всё было наоборот: крупный зелёный балл сверху, а перечисление того, чего в
 * комплекте не хватает, — мелким текстом под ним.
 */
const OutfitReadinessPanel: React.FC<Props> = ({readiness, constraints, onComplete, updating}) => {
    const ready = isReady(readiness);
    const gaps = readiness?.gaps ?? [];
    const satisfied = readiness?.satisfied ?? [];
    const unchecked = readiness?.unchecked ?? [];
    const recognized = constraints?.recognized ?? [];

    const tone = ready
        ? 'border-green-300 dark:border-green-500/40 bg-green-50 dark:bg-green-900/20'
        : 'border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20';

    return (
        <section className={`rounded-2xl border p-4 space-y-3 ${tone}`} data-testid="outfit-readiness">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                    {ready
                        ? <CheckCheckIcon className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5"/>
                        : <AlertTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"/>}
                    <div>
                        <p className={`font-semibold ${ready
                            ? 'text-green-800 dark:text-green-300'
                            : 'text-amber-800 dark:text-amber-300'}`} data-testid="readiness-status">
                            {updating ? 'Проверяем комплект…' : readinessLabel(readiness)}
                        </p>
                        {!ready && !updating && gaps.length > 0 && (
                            <p className="text-sm text-amber-800 dark:text-amber-300" data-testid="readiness-missing">
                                Не хватает: {missingLabel(readiness)}
                            </p>
                        )}
                    </div>
                </div>
                {!ready && !updating && onComplete && (
                    <button type="button" onClick={onComplete} data-testid="readiness-add-btn"
                            className="min-h-[44px] px-4 rounded-xl bg-brand-600 text-white hover:bg-brand-700 text-sm font-medium inline-flex items-center gap-2">
                        <PlusIcon className="w-4 h-4"/> Добавить
                    </button>
                )}
            </div>

            {!updating && gaps.length > 0 && (
                <ul className="space-y-1.5" data-testid="readiness-gaps">
                    {gaps.map(gap => (
                        <li key={gap.role} className="text-sm text-amber-800 dark:text-amber-300">
                            <span className="font-medium">{gap.label}.</span>{' '}
                            <span className="text-amber-700 dark:text-amber-400">{gap.reason}</span>
                        </li>
                    ))}
                </ul>
            )}

            {(recognized.length > 0 || satisfied.length > 0) && !updating && (
                <div className="space-y-1.5" data-testid="readiness-satisfied">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Учтено</p>
                    <ul className="space-y-1">
                        {[...recognized, ...satisfied].map((text, i) => (
                            <li key={`s${i}`} className="flex gap-2 text-xs text-gray-700 dark:text-gray-300">
                                <CheckCheckIcon className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5"/>
                                <span>{text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {unchecked.length > 0 && !updating && (
                <div className="space-y-1.5" data-testid="readiness-unchecked">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Не проверено</p>
                    <ul className="space-y-1">
                        {unchecked.map((text, i) => (
                            <li key={`u${i}`} className="flex gap-2 text-xs text-gray-500 dark:text-gray-400">
                                <CloudIcon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400 dark:text-ink-500"/>
                                <span>{text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};

export default OutfitReadinessPanel;
